import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    if (user) api.get("/me/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
  }, [user]);

  const subscribed = (id) => subs.some((s) => s.package_id === id && s.status === "active");

  const buy = async (pkg) => {
    if (!user) return navigate("/login");
    if (user.role === "admin" && !user.impersonated_by) { toast.info("Admins manage pricing in the console; purchases are made by customers.", { action: { label: "Open pricing admin", onClick: () => navigate("/admin") } }); return; }
    setBusy(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <section className="max-w-[1400px] mx-auto px-6 py-24">
        <p className="overline text-[#7C3AED] mb-3">Pricing · Scale on demand</p>
        <h1 className="font-heading font-bold tracking-tighter text-5xl sm:text-6xl mb-4">Choose your testing package</h1>
        <p className="text-secondary max-w-2xl mb-14 text-lg">
          One-time onboarding plus monthly usage. Cancel anytime. Every package includes release
          impact analysis and full test evidence.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          {packages.map((pkg) => (
            <div key={pkg.id} className="card-soft p-10 hover-lift relative" data-testid={`pricing-card-${pkg.id}`}>
              <div className="h-1 w-16 mb-6 bg-primary" />
              <p className="font-mono text-xs text-secondary mb-4">{pkg.code}</p>
              <h3 className="font-heading font-bold text-3xl">{pkg.name}</h3>
              <p className="text-secondary mb-8">{pkg.tagline}</p>
              <div className="font-heading font-bold text-5xl mb-1">
                ${pkg.monthly_price.toLocaleString()}
                <span className="text-lg text-secondary font-normal">/month</span>
              </div>
              <p className="text-sm text-secondary mb-8">+ one-time onboarding · <a href="/#contact" className="text-[#3A2DB8] hover:text-[#FF6B3D] font-medium" data-testid={`onboarding-contact-${pkg.id}`}>talk to us for a quote</a></p>
              <ul className="space-y-3 mb-10">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm"><Check className="w-4 h-4 text-[#0F9D77]" /> {f}</li>
                ))}
              </ul>
              {subscribed(pkg.id) ? (
                <Button disabled className="rounded-sm w-full h-12 bg-[#0F9D77]/15 text-[#0F9D77] border border-[#0F9D77]/30" data-testid={`subscribed-${pkg.id}`}>
                  <Check className="w-4 h-4 mr-2" /> Active subscription
                </Button>
              ) : (
                <Button onClick={() => buy(pkg)} disabled={busy === pkg.id} data-testid={`buy-btn-${pkg.id}`}
                  className="rounded-sm w-full h-12 bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold gap-2">
                  {busy === pkg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Buy package <ArrowRight className="w-4 h-4" /></>}
                </Button>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-secondary mt-8 font-mono">Test card: 4242 4242 4242 4242 · any future expiry · any CVC</p>
      </section>
      <Footer />
    </div>
  );
}
